import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import {
  ActionModalType,
  ActionModalMode,
  ActionModalButton,
  ActionModalResult,
  ActionModalIconAnimation
} from './action-modal.model';

@Component({
  selector: 'app-action-modal',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './action-modal.component.html',
  styleUrls: ['./action-modal.component.scss']
})
export class ActionModalComponent {
  // ---- all of these are set via BsModalService's `initialState` ----
  type: ActionModalType = 'info';
  mode: ActionModalMode = 'ok';
  title = '';
  message = '';
  icon?: string;
  iconColor?: string;
  iconAnimation?: ActionModalIconAnimation;
  titleColor?: string;
  messageColor?: string;
  confirmColor?: string;
  confirmText?: string;
  cancelText?: string;
  extraText?: string;
  hideIcon = false;
  hideCloseButton = true;
  buttons: ActionModalButton[] = [];

  /** subscribe via `modalRef.content.onClose` to get the result */
  public onClose: Subject<ActionModalResult> = new Subject<ActionModalResult>();

  private readonly defaultIcons: Record<ActionModalType, string> = {
    success: 'boxicons:badge-check-filled',
    error: 'material-symbols:error-rounded',
    warning: 'ep:warning-filled',
    info: 'ep:info-filled',
    question: 'ep:question-filled',
    delete: 'ep:delete-filled',
    loading: 'ix:control-spinner'
  };

  private readonly defaultIconAnimations: Record<ActionModalType, ActionModalIconAnimation> = {
    success: 'pop',
    error: 'shake',
    warning: 'pulse',
    info: 'pop',
    question: 'pop',
    delete: 'shake',
    loading: 'spin'
  };
  // Neutral system palette — matches change-password modal's #595959 header icon.
  // error/delete stay red as a visual warning; success is green; loading is neutral gray.
  private readonly defaultColors: Record<ActionModalType, string> = {
    success: '#1faa59',
    error: '#dc3545',
    warning: '#1a1a1a',
    info: '#1a1a1a',
    question: '#1a1a1a',
    delete: '#dc3545',
    loading: '#595959'
  };

  // label of the single button in mode 'ok' (falls back to 'OK')
  private readonly defaultOkText: Partial<Record<ActionModalType, string>> = {
    error: 'Close',
    success: 'Done'
  };

  private readonly hexColorPattern = /^#([0-9a-f]{6})$/i;
  private readonly okButtonColor = '#595959';

  constructor(public bsModalRef: BsModalRef) {}

  get resolvedIcon(): string {
    return this.icon || this.defaultIcons[this.type] || this.defaultIcons.info;
  }

  get resolvedColor(): string {
    const candidate = this.iconColor || this.defaultColors[this.type] || this.defaultColors.info;
    return this.hexColorPattern.test(candidate) ? candidate : this.defaultColors.info;
  }

  /**
   * Button background. A valid `confirmColor` always wins; otherwise the single-button
   * ('ok') modals use gray, and yes/no modals follow the icon color (red for delete).
   */
  get resolvedConfirmColor(): string {
    if (this.confirmColor && this.hexColorPattern.test(this.confirmColor)) {
      return this.confirmColor;
    }
    return this.mode === 'ok' ? this.okButtonColor : this.resolvedColor;
  }

  get okText(): string {
    return this.confirmText || this.defaultOkText[this.type] || 'OK';
  }

  get isSpinning(): boolean {
    return this.type === 'loading';
  }

  /** number of footer columns; 0 means the footer is not rendered */
  get buttonCount(): number {
    switch (this.mode) {
      case 'ok': return 1;
      case 'yesno': return 2;
      case 'yesnocancel': return 3;
      case 'custom': return this.buttons.length;
      default: return 0;
    }
  }

  get resolvedIconAnimation(): ActionModalIconAnimation {
    return this.iconAnimation || this.defaultIconAnimations[this.type] || 'none';
  }

  /** true for error/delete — used to keep the red accent on destructive actions only */
  get isDestructive(): boolean {
    return this.type === 'error' || this.type === 'delete';
  }

    /** null = no inline style, so the SCSS default applies */
  get resolvedTitleColor(): string | null {
    return this.titleColor && this.hexColorPattern.test(this.titleColor) ? this.titleColor : null;
  }

  get resolvedMessageColor(): string | null {
    return this.messageColor && this.hexColorPattern.test(this.messageColor) ? this.messageColor : null;
  }

  confirm(): void {
    this.emitAndClose(true);
  }

  cancel(): void {
    this.emitAndClose(false);
  }

  extra(): void {
    this.emitAndClose('extra');
  }

  customClick(btn: ActionModalButton): void {
    this.emitAndClose(btn.value ?? btn.text);
  }

  private emitAndClose(result: boolean | string): void {
    this.onClose.next({ result });
    this.onClose.complete();
    this.bsModalRef.hide();
  }
}